import { LANG_NUMERALS } from "@/constants/locales";
import { LangKey } from "./types/LangKey";
import { SupportedLocale } from "./types/SupportedLocale";
import detectLanguage from "@/utils/detectLanguage";
import flattenDictionary from "@/utils/flattenDictionary";
import { translateNumerals } from "@/utils/translateNumerals";
import { create } from "zustand";

export interface i18nState {
  locale: SupportedLocale;
  dictionaries: Partial<Record<SupportedLocale, Record<LangKey, string>>>;
}

const lazyImports = {
  en: () => import("@/locales/en"),
  ar: () => import("@/locales/ar"),
};

export interface i18nActions {
  t: (key: LangKey, locale?: SupportedLocale) => string | LangKey;
  tNum: (num: number | bigint | string) => string;

  setLocale: (locale: SupportedLocale) => void;
  loadDictionary: (locale: SupportedLocale) => Promise<void>;
}

export const useI18nStore = create<i18nState & i18nActions>()((set, get) => ({
  locale: detectLanguage().code,
  dictionaries: {},

  t: (key: LangKey, locale?: SupportedLocale) => {
    return get().dictionaries[locale ?? get().locale]?.[key] ?? key;
  },

  tNum: (num: number | bigint | string) => {
    const { locale } = get();
    if (!LANG_NUMERALS[locale]) {
      return num.toString();
    }

    return translateNumerals(num, LANG_NUMERALS[locale]);
  },

  setLocale: async function (locale) {
    document.documentElement.classList.remove(get().locale, get().locale.slice(0, 2));

    if (!(locale in get().dictionaries)) {
      await get().loadDictionary(locale);
    }

    set((state) => ({
      ...state,
      locale: locale,
    }));

    document.documentElement.classList.add(locale, locale.slice(0, 2));
    document.documentElement.lang = locale.slice(0, 2);
    localStorage.setItem("locale", locale);
  },

  loadDictionary: async function (locale) {
    const rawDictionary = await lazyImports[locale]().then((m) => m.default);
    const processedDictionary = flattenDictionary(rawDictionary);

    set((state) => ({
      ...state,
      dictionaries: {
        ...state.dictionaries,
        [locale]: processedDictionary,
      },
    }));
  },
}));

export const getI18n = useI18nStore.getState;
